import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GoogleLoginButton from '../components/GoogleLoginButton';
import api from '../services/api';
import '../styles/Login.css';
import i18n from '../i18n'; // ou le chemin correct vers ton fichier i18n
const Login = () => {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  
  // États du composant
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [lastAttemptEmail, setLastAttemptEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // 🔥 Charger les credentials sauvegardés au montage
  useEffect(() => {
    console.log('🔍 Vérification Remember Me au chargement...');
    
    const savedCredentials = localStorage.getItem('rememberMe');
    if (savedCredentials) {
      try {
        const { email, password, timestamp } = JSON.parse(savedCredentials);
        
        // Vérifier si le remember me n'a pas expiré (30 jours)
        const expirationTime = 30 * 24 * 60 * 60 * 1000; // 30 jours en ms
        const isExpired = Date.now() - timestamp > expirationTime;
        
        if (!isExpired) {
          setFormData({ email, password });
          setRememberMe(true);
          setLastAttemptEmail(email);
          console.log('✅ Credentials restaurés depuis localStorage');
          console.log('📧 Email restauré:', email);
        } else {
          localStorage.removeItem('rememberMe');
          console.log('⏰ Remember me expiré (>30 jours), suppression');
        }
      } catch (error) {
        console.error('❌ Erreur lecture remember me:', error);
        localStorage.removeItem('rememberMe');
      }
    } else {
      console.log('ℹ️ Aucun Remember Me trouvé');
    }
  }, []);

  // Gestion des changements d'input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gestion du checkbox Remember Me
  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    console.log('💾 Remember Me:', checked ? 'ACTIVÉ' : 'DÉSACTIVÉ');
  };

  // Fonction pour masquer l'email (sécurité)
  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    const maskedUsername = username.length > 2 
      ? username[0] + '*'.repeat(username.length - 2) + username[username.length - 1]
      : username;
    return `${maskedUsername}@${domain}`;
  };

  // Fonction de détection admin (avec feedback visuel)
  const getLoginHint = () => {
    if (formData.email.includes('admin') || 
        formData.email.includes('draguemaster.com') || 
        formData.email.includes('jfelsondev@gmail.com')) {
      return (
        <div className="login-hint admin">
          🛡️ {t('auth:login.admin_detected')}
          <div className="admin-hint-subtitle">
            {t('auth:login.admin_redirect')}
          </div>
        </div>
      );
    }
    return null;
  };

  // Gestion du mot de passe oublié
  const handleForgotPassword = async () => {
    console.log('===== DÉBUT FORGOT PASSWORD =====');
    console.log('📧 Email pour reset:', lastAttemptEmail);
    
    if (!lastAttemptEmail) {
        console.log('❌ Aucun email détecté');
        setMessage({ 
          type: 'error', 
          text: t('auth:forgot_password.no_email_detected')
        });
        return;
    }

    setLoading(true);
    setMessage({ type: 'info', text: t('auth:forgot_password.sending') });
    
    try {
        const response = await api.post('/auth/reset-password', {
            email: lastAttemptEmail
        });

        console.log('📡 Réponse serveur reset:', {
          status: response.status,
          ok: response.ok
        });

        const data = response.data;
        console.log('📋 Data reset:', data);
        
        if (data.success) {
            console.log('✅ Reset password envoyé avec succès');
            setMessage({ 
                type: 'success', 
                text: t('auth:forgot_password.link_sent', { email: maskEmail(lastAttemptEmail) })
            });
            setShowForgotPassword(false);
        } else {
            console.log('❌ Échec reset password:', data.message);
            setMessage({ 
              type: 'error', 
              text: data.message || t('auth:forgot_password.send_failed')
            });
        }
    } catch (error) {
        console.error('🔥 Erreur réseau reset:', error);
        setMessage({ 
          type: 'error', 
          text: t('errors:network')
        });
    } finally {
        setLoading(false);
        console.log('===== FIN FORGOT PASSWORD =====');
    }
  };

  // Fonction principale de connexion
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation côté client
    if (!formData.email || !formData.password) {
      setMessage({
        type: 'error',
        text: t('auth:messages.email_required')
      });
      setLoading(false);
      return;
    }

    // Validation format email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({
        type: 'error',
        text: t('auth:messages.invalid_email')
      });
      setLoading(false);
      return;
    }

    // Stocker l'email pour utilisation ultérieure
    setLastAttemptEmail(formData.email);

    console.log('🔍 === DÉBUT TENTATIVE DE CONNEXION ===');
    console.log('📧 Email:', formData.email);
    console.log('🔑 Password:', '***'.repeat(formData.password.length));
    console.log('💾 Remember Me:', rememberMe ? 'OUI' : 'NON');
    console.log('🕐 Timestamp:', new Date().toISOString());

    try {
      console.log('📡 Envoi requête vers API...');
      
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });

      console.log('✅ Réponse reçue du serveur');
      console.log('📡 Status:', response.status);
      console.log('📡 StatusText:', response.statusText);

      const data = response.data;
      
      console.log('📋 === DONNÉES REÇUES ===');
      console.log('success:', data.success);
      console.log('message:', data.message);
      console.log('token présent?', !!data.token);
      console.log('user présent?', !!data.user);
      
      if (data.user) {
        console.log('👤 User ID:', data.user.id);
        console.log('📧 User Email:', data.user.email);
        console.log('🎭 User Role:', data.user.role);
        console.log('👤 Username:', data.user.username);
      }

      // Condition simplifiée
      if (data.success && data.token && data.user) {
        console.log('✅ ✅ ✅ CONNEXION RÉUSSIE !');
        
        setMessage({
          type: 'success',
          text: data.message || t('auth:messages.login_success')
        });

        // ========================================
        // 🔥🔥🔥 LIGNES 230-317 - CODE COMPLET 🔥🔥🔥
        // ========================================

        // Gestion Remember Me
        if (rememberMe) {
          const credentialsToSave = {
            email: formData.email,
            password: formData.password,
            timestamp: Date.now()
          };
          localStorage.setItem('rememberMe', JSON.stringify(credentialsToSave));
          console.log('💾 Credentials sauvegardés dans localStorage');
        } else {
          localStorage.removeItem('rememberMe');
          console.log('🗑️ Remember Me désactivé, credentials supprimés');
        }

        // Sauvegarder les données d'authentification
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user.preferred_language) {
          localStorage.setItem('language', data.user.preferred_language);
          i18n.changeLanguage(data.user.preferred_language);
        }
        console.log('💾 Token sauvegardé dans localStorage');
        console.log('💾 User sauvegardé dans localStorage');

        // Afficher warnings si mot de passe temporaire
        if (data.warnings && data.warnings.length > 0) {
          console.log('⚠️ Avertissements:', data.warnings);
          data.warnings.forEach(warning => {
            console.log(`  ⚠️ ${warning}`);
          });
        }

        // ========================================
        // 🔥 REDIRECTION CONDITIONNELLE BASÉE SUR LE RÔLE
        // ========================================
        console.log('\n🔍 === ANALYSE REDIRECTION ===');
        const userRole = data.user.role || 'user';
        console.log('🎭 Rôle détecté:', userRole);

        // Déterminer la destination selon le rôle
        let redirectPath;
        let redirectMessage;

        if (userRole === 'super_admin' || userRole === 'admin') {
          // ✅ ADMIN → Dashboard Admin
          redirectPath = '/admin';
          redirectMessage = '🛡️ Redirection vers Admin Dashboard...';
          console.log('✅ Utilisateur ADMIN détecté');
          console.log('→ Destination: /admin');
        } else {
          // ✅ USER → Dashboard Utilisateur
          redirectPath = '/dashboard';
          redirectMessage = '📄 Redirection vers User Dashboard...';
          console.log('✅ Utilisateur STANDARD détecté');
          console.log('→ Destination: /dashboard');
        }

        console.log(redirectMessage);
        console.log('⏱️ Redirection dans 1.5 secondes...');
        console.log('=================================\n');

        // Redirection après 1.5 secondes
        setTimeout(() => {
          console.log(`🚀 REDIRECTION EN COURS vers ${redirectPath}`);
          navigate(redirectPath);
        }, 1500);

        // ========================================
        // FIN DES LIGNES 230-317
        // ========================================

      } else {
        // Réponse invalide du serveur
        console.log('❌ Réponse serveur invalide');
        console.log('Missing fields:', {
          success: !data.success,
          token: !data.token,
          user: !data.user
        });
        
        setMessage({
          type: 'error',
          text: data.message || t('auth:messages.invalid_response')
        });
      }
    } catch (error) {
      console.error('🔥 === ERREUR DÉTECTÉE ===');
      console.error('Type:', error.name);
      console.error('Message:', error.message);
      
      if (error.response) {
        // Le serveur a répondu avec un code d'erreur
        console.error('📡 Réponse erreur du serveur');
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
        
        setMessage({
          type: 'error',
          text: error.response.data?.message || t('auth:messages.invalid_credentials')
        });
        
        // Afficher le bouton immédiatement pour toute erreur de connexion
        if (formData.email && (error.response.status === 400 || error.response.status === 401 || error.response.status === 403)) {
          setShowForgotPassword(true);
        }
        
      } else if (error.request) {
        // Pas de réponse du serveur (problème réseau)
        console.error('📡 Pas de réponse du serveur');
        
        setMessage({
          type: 'error',
          text: t('errors:network')
        });
        
        // Afficher le bouton si on a un email
        if (formData.email) {
          setShowForgotPassword(true);
        }
        
      } else {
        // Erreur de configuration
        console.error('🔥 Erreur configuration:', error.message);
        
        setMessage({
          type: 'error',
          text: t('errors:generic')
        });
        
        // Afficher le bouton aussi pour les erreurs de config
        if (formData.email) {
          setShowForgotPassword(true);
        }
      }
      
      console.error('Stack:', error.stack);
      
    } finally {
      setLoading(false);
      console.log('🔍 === FIN TENTATIVE DE CONNEXION ===');
    }
  };

  return (
    <div className="login-page">
      <div className={`login-container ${loading ? 'loading' : ''}`}>
        {/* En-tête avec branding */}
        <div className="login-header">
          <img src="/logo512.png" alt="RizzingBot" style={{ width: 120, height: 120, objectFit: 'contain', marginBottom: 8 }} />
          <h1 className="login-title">🔐 {t('auth:login.title_full')}</h1>
          <p className="login-subtitle">
            {t('auth:login.subtitle')}
          </p>
        </div>
        
        <div className="social-login-section">
            <GoogleLoginButton text={t('auth:login.google_login')} />
            <p className="terms-notice">
              By continuing, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
            
            <div className="divider">
              <span>{t('common:common.or')}</span>
            </div>
        </div>
        
        {/* Messages de feedback avec états détaillés */}
        {message.text && (
          <div className={`login-message ${message.type}`}>
            <span className="message-icon">
              {message.type === 'success' && '✅'}
              {message.type === 'error' && '❌'}
              {message.type === 'info' && 'ℹ️'}
            </span>
            <span className="message-text">{message.text}</span>
          </div>
        )}

        {/* Hint de détection administrateur */}
        {getLoginHint()}

        {/* Formulaire de connexion */}
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-form-group">
            <label className="login-form-label" htmlFor="email">
              📧 {t('auth:login.email_label')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              className="login-form-input"
              placeholder={t('auth:login.email_placeholder')}
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={loading}
              autoComplete="username"
            />
            {formData.email && (
              <div className="input-feedback">
                {formData.email.includes('admin') ? `🛡️ ${t('auth:login.admin_detected_short')}` : `👤 ${t('auth:login.user_standard')}`}
              </div>
            )}
          </div>

          <div className="login-form-group">
            <label className="login-form-label" htmlFor="password">
              🔒 {t('auth:login.password_label')}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              className="login-form-input"
              placeholder={t('auth:login.password_placeholder')}
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={loading}
              autoComplete="current-password"
            />
            {formData.password && (
              <div className="input-feedback">
                🔑 {t('auth:login.password_length', { count: formData.password.length })}
              </div>
            )}
          </div>

          {/* Checkbox Remember Me */}
          <div className="login-remember-me">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={handleRememberMeChange}
                disabled={loading}
                className="login-checkbox"
              />
              <span className="login-checkbox-text">
                💾 {t('auth:login.remember_me')}
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="login-button-icon spinning">⏳</span>
                {t('common:common.loading')}
              </>
            ) : (
              <>
                <span className="login-button-icon">🚀</span>
                {t('auth:login.login_button')}
              </>
            )}
          </button>
        </form>

        {/* Section mot de passe oublié (apparaît après échec) */}
        {showForgotPassword && lastAttemptEmail && (
          <div className="forgot-password-section">
            <div className="forgot-password-card">
              <h3>🔑 {t('auth:login.forgot_password_title')}</h3>
              <p>
                {t('auth:login.forgot_password_message')}
              </p>
              <div className="email-preview">
                📧 <strong>{maskEmail(lastAttemptEmail)}</strong>
              </div>
              <div className="forgot-password-actions">
                <button 
                  onClick={handleForgotPassword}
                  className="send-reset-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>⏳ {t('auth:login.sending')}</>
                  ) : (
                    <>📧 {t('auth:login.send_reset')}</>
                  )}
                </button>
                <button 
                  onClick={() => setShowForgotPassword(false)}
                  className="cancel-button"
                  type="button"
                >
                  ❌ {t('common:buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lien vers inscription */}
        <div className="login-link">
          <p>{t('auth:login.no_account')}</p>
          <Link to="/signup" className="signup-link">
            ✨ {t('auth:login.signup_link')}
          </Link>
        </div>

          {/* 🆕 Lien discret vers espace partenaire */}
        <div className="login-partner-link">
          <Link to="/affiliates/login">
            🤝 Espace partenaire
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;